/*!
 * devextreme-angular
 * Version: 26.1.6
 * Build date: Fri Oct 16 2026
 *
 * Copyright (c) 2012 - 2026 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/DevExtreme
 */

/* tslint:disable:max-line-length */


import {
    TransferState,
    Component,
    NgModule,
    ElementRef,
    NgZone,
    PLATFORM_ID,
    Inject,

    Input,
    Output,
    OnDestroy,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import type FileSystemItem from 'devextreme/file_management/file_system_item';
import type { dxFileManagerContextMenu, dxFileManagerDetailsColumn, FileManagerItemViewMode, ContentReadyEvent, ContextMenuItemClickEvent, ContextMenuShowingEvent, CurrentDirectoryChangedEvent, DirectoryCreatedEvent, DirectoryCreatingEvent, DisposingEvent, ErrorOccurredEvent, FileUploadedEvent, FileUploadingEvent, FocusedItemChangedEvent, InitializedEvent, ItemCopiedEvent, ItemCopyingEvent, ItemDeletedEvent, ItemDeletingEvent, ItemDownloadingEvent, ItemMovedEvent, ItemMovingEvent, ItemRenamedEvent, ItemRenamingEvent, OptionChangedEvent, SelectedFileOpenedEvent, SelectionChangedEvent, ToolbarItemClickEvent, dxFileManagerToolbar } from 'devextreme/ui/file_manager';
import type { SingleOrMultiple } from 'devextreme/common';

import DxFileManager from 'devextreme/ui/file_manager';


import {
    DxComponent,
    DxTemplateHost,
    DxIntegrationModule,
    DxTemplateModule,
    NestedOptionHost,
    IterableDifferHelper,
    WatcherHelper,
    CollectionNestedOption,
} from 'devextreme-angular/core';

import { DxoContextMenuModule } from 'devextreme-angular/ui/nested';
import { DxiItemModule } from 'devextreme-angular/ui/nested';
import { DxoItemViewModule } from 'devextreme-angular/ui/nested';
import { DxoDetailsModule } from 'devextreme-angular/ui/nested';
import { DxiColumnModule } from 'devextreme-angular/ui/nested';
import { DxoNotificationsModule } from 'devextreme-angular/ui/nested';
import { DxoPermissionsModule } from 'devextreme-angular/ui/nested';
import { DxoToolbarModule } from 'devextreme-angular/ui/nested';
import { DxiFileSelectionItemModule } from 'devextreme-angular/ui/nested';
import { DxoUploadModule } from 'devextreme-angular/ui/nested';

import { DxiFileManagerColumnModule } from 'devextreme-angular/ui/file-manager/nested';
import { DxoFileManagerContextMenuModule } from 'devextreme-angular/ui/file-manager/nested';
import { DxiFileManagerContextMenuItemModule } from 'devextreme-angular/ui/file-manager/nested';
import { DxoFileManagerDetailsModule } from 'devextreme-angular/ui/file-manager/nested';
import { DxiFileManagerFileSelectionItemModule } from 'devextreme-angular/ui/file-manager/nested';
import { DxiFileManagerItemModule } from 'devextreme-angular/ui/file-manager/nested';
import { DxoFileManagerItemViewModule } from 'devextreme-angular/ui/file-manager/nested';
import { DxoFileManagerNotificationsModule } from 'devextreme-angular/ui/file-manager/nested';
import { DxoFileManagerPermissionsModule } from 'devextreme-angular/ui/file-manager/nested';
import { DxoFileManagerToolbarModule } from 'devextreme-angular/ui/file-manager/nested';
import { DxiFileManagerToolbarItemModule } from 'devextreme-angular/ui/file-manager/nested';
import { DxoFileManagerUploadModule } from 'devextreme-angular/ui/file-manager/nested';
import { 
           PROPERTY_TOKEN_columns,
           PROPERTY_TOKEN_items,
           PROPERTY_TOKEN_fileSelectionItems,
     } from 'devextreme-angular/core/tokens';


/**
 * The FileManager is a UI component that allows users to upload, select, and manage files and directories in different file storages.

 */
@Component({
    selector: 'dx-file-manager',
    template: '',
    host: { ngSkipHydration: 'true' },
    imports: [ DxIntegrationModule ],
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
})
export class DxFileManagerComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {

    @ContentChildren(PROPERTY_TOKEN_columns)
    set _columnsContentChildren(value: QueryList<CollectionNestedOption>) {
        this.setChildren('columns', value);
    }

    @ContentChildren(PROPERTY_TOKEN_items)
    set _itemsContentChildren(value: QueryList<CollectionNestedOption>) {
        this.setChildren('items', value);
    }

    @ContentChildren(PROPERTY_TOKEN_fileSelectionItems)
    set _fileSelectionItemsContentChildren(value: QueryList<CollectionNestedOption>) {
        this.setChildren('fileSelectionItems', value);
    }

    instance: DxFileManager = null;

    /**
     * Specifies the shortcut key that sets focus on the UI component.
    
     */
    @Input()
    get accessKey(): string | undefined {
        return this._getOption('accessKey');
    }
    set accessKey(value: string | undefined) {
        this._setOption('accessKey', value);
    }


    /**
     * Specifies whether the UI component changes its visual state as a result of user interaction.
    
     */
    @Input()
    get activeStateEnabled(): boolean {
        return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value: boolean) {
        this._setOption('activeStateEnabled', value);
    }


    /**
     * Specifies the allowed upload file extensions.
    
     */
    @Input()
    get allowedFileExtensions(): Array<string> {
        return this._getOption('allowedFileExtensions');
    }
    set allowedFileExtensions(value: Array<string>) {
        this._setOption('allowedFileExtensions', value);
    }


    /**
     * Configures the context menu settings.
    
     */
    @Input()
    get contextMenu(): dxFileManagerContextMenu {
        return this._getOption('contextMenu');
    }
    set contextMenu(value: dxFileManagerContextMenu) {
        this._setOption('contextMenu', value);
    }


    /**
     * Specifies the path that is used when the FileManager is initialized.
    
     */
    @Input()
    get currentPath(): string {
        return this._getOption('currentPath');
    }
    set currentPath(value: string) {
        this._setOption('currentPath', value);
    }


    /**
     * Specifies an array of path keys to the current location.
    
     */
    @Input()
    get currentPathKeys(): Array<string> {
        return this._getOption('currentPathKeys');
    }
    set currentPathKeys(value: Array<string>) {
        this._setOption('currentPathKeys', value);
    }


    /**
     * Customizes columns in details view. Applies only if itemView.mode is &apos;details&apos;.
    
     */
    @Input()
    get customizeDetailColumns(): ((columns: Array<dxFileManagerDetailsColumn>) => Array<dxFileManagerDetailsColumn>) {
        return this._getOption('customizeDetailColumns');
    }
    set customizeDetailColumns(value: ((columns: Array<dxFileManagerDetailsColumn>) => Array<dxFileManagerDetailsColumn>)) {
        this._setOption('customizeDetailColumns', value);
    }


    /**
     * Allows you to provide custom icons to be used as thumbnails.
    
     */
    @Input()
    get customizeThumbnail(): ((fileSystemItem: FileSystemItem) => string) {
        return this._getOption('customizeThumbnail');
    }
    set customizeThumbnail(value: ((fileSystemItem: FileSystemItem) => string)) {
        this._setOption('customizeThumbnail', value);
    }


    /**
     * Specifies whether the UI component responds to user interaction.
    
     */
    @Input()
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the global attributes to be attached to the UI component&apos;s container element.
    
     */
    @Input()
    get elementAttr(): Record<string, any> {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: Record<string, any>) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies the file system provider.
    
     */
    @Input()
    get fileSystemProvider(): any | null {
        return this._getOption('fileSystemProvider');
    }
    set fileSystemProvider(value: any | null) {
        this._setOption('fileSystemProvider', value);
    }


    /**
     * Specifies a key of the initially or currently focused item.
    
     */
    @Input()
    get focusedItemKey(): string | undefined {
        return this._getOption('focusedItemKey');
    }
    set focusedItemKey(value: string | undefined) {
        this._setOption('focusedItemKey', value);
    }


    /**
     * Specifies whether the UI component can be focused using keyboard navigation.
    
     */
    @Input()
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * Specifies the UI component&apos;s height.
    
     */
    @Input()
    get height(): number | string | undefined {
        return this._getOption('height');
    }
    set height(value: number | string | undefined) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the UI component.
    
     */
    @Input()
    get hint(): string | undefined {
        return this._getOption('hint');
    }
    set hint(value: string | undefined) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the UI component changes its state when a user pauses on it.
    
     */
    @Input()
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * Configures the file and directory view.
    
     */
    @Input()
    get itemView(): { details?: { columns?: Array<dxFileManagerDetailsColumn | string> }, mode?: FileManagerItemViewMode, showFolders?: boolean, showParentFolder?: boolean } {
        return this._getOption('itemView');
    }
    set itemView(value: { details?: { columns?: Array<dxFileManagerDetailsColumn | string> }, mode?: FileManagerItemViewMode, showFolders?: boolean, showParentFolder?: boolean }) {
        this._setOption('itemView', value);
    }


    /**
     * Configures notification settings.
    
     */
    @Input()
    get notifications(): { showPanel?: boolean, showPopup?: boolean } {
        return this._getOption('notifications');
    }
    set notifications(value: { showPanel?: boolean, showPopup?: boolean }) {
        this._setOption('notifications', value);
    }


    /**
     * Specifies actions that a user is allowed to perform on files and directories.
    
     */
    @Input()
    get permissions(): { copy?: boolean, create?: boolean, delete?: boolean, download?: boolean, move?: boolean, rename?: boolean, upload?: boolean } {
        return this._getOption('permissions');
    }
    set permissions(value: { copy?: boolean, create?: boolean, delete?: boolean, download?: boolean, move?: boolean, rename?: boolean, upload?: boolean }) {
        this._setOption('permissions', value);
    }


    /**
     * Specifies the root directory display name.
    
     */
    @Input()
    get rootFolderName(): string {
        return this._getOption('rootFolderName');
    }
    set rootFolderName(value: string) {
        this._setOption('rootFolderName', value);
    }


    /**
     * Switches the UI component to a right-to-left representation.
    
     */
    @Input()
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Contains an array of initially or currently selected files and directories&apos; keys.
    
     */
    @Input()
    get selectedItemKeys(): Array<string> {
        return this._getOption('selectedItemKeys');
    }
    set selectedItemKeys(value: Array<string>) {
        this._setOption('selectedItemKeys', value);
    }


    /**
     * Specifies whether a user can select a single or multiple files and directories in the item view simultaneously.
    
     */
    @Input()
    get selectionMode(): SingleOrMultiple {
        return this._getOption('selectionMode');
    }
    set selectionMode(value: SingleOrMultiple) {
        this._setOption('selectionMode', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
    
     */
    @Input()
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * Configures toolbar settings.
    
     */
    @Input()
    get toolbar(): dxFileManagerToolbar {
        return this._getOption('toolbar');
    }
    set toolbar(value: dxFileManagerToolbar) {
        this._setOption('toolbar', value);
    }


    /**
     * Configures upload settings.
    
     */
    @Input()
    get upload(): { chunkSize?: number, maxFileSize?: number } {
        return this._getOption('upload');
    }
    set upload(value: { chunkSize?: number, maxFileSize?: number }) {
        this._setOption('upload', value);
    }


    /**
     * Specifies whether the UI component is visible.
    
     */
    @Input()
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the UI component&apos;s width.
    
     */
    @Input()
    get width(): number | string | undefined {
        return this._getOption('width');
    }
    set width(value: number | string | undefined) {
        this._setOption('width', value);
    }

    /**
    
     * A function that is executed when the UI component is rendered and each time the component is repainted.
    
    
     */
    @Output() onContentReady: EventEmitter<ContentReadyEvent>;

    /**
    
     * A function that is executed when a context menu item is clicked.
    
    
     */
    @Output() onContextMenuItemClick: EventEmitter<ContextMenuItemClickEvent>;

    /**
    
     * A function that is executed before a context menu is displayed.
    
    
     */
    @Output() onContextMenuShowing: EventEmitter<ContextMenuShowingEvent>;

    /**
    
     * A function that is executed when the current directory is changed.
    
    
     */
    @Output() onCurrentDirectoryChanged: EventEmitter<CurrentDirectoryChangedEvent>;

    /**
    
     * A function that is executed when a directory is created.
    
    
     */
    @Output() onDirectoryCreated: EventEmitter<DirectoryCreatedEvent>;

    /**
    
     * A function that is executed before a directory is created.
    
    
     */
    @Output() onDirectoryCreating: EventEmitter<DirectoryCreatingEvent>;

    /**
    
     * A function that is executed before the UI component is disposed of.
    
    
     */
    @Output() onDisposing: EventEmitter<DisposingEvent>;

    /**
    
     * A function that is executed when an error occurs.
    
    
     */
    @Output() onErrorOccurred: EventEmitter<ErrorOccurredEvent>;

    /**
    
     * A function that is executed when a file is successfully uploaded.
    
    
     */
    @Output() onFileUploaded: EventEmitter<FileUploadedEvent>;

    /**
    
     * A function that is executed before the file is uploaded.
    
    
     */
    @Output() onFileUploading: EventEmitter<FileUploadingEvent>;

    /**
    
     * A function that is executed when the focused item is changed.
    
    
     */
    @Output() onFocusedItemChanged: EventEmitter<FocusedItemChangedEvent>;

    /**
    
     * A function used in JavaScript frameworks to save the UI component instance.
    
    
     */
    @Output() onInitialized: EventEmitter<InitializedEvent>;

    /**
    
     * A function that is executed when a file or directory is copied.
    
    
     */
    @Output() onItemCopied: EventEmitter<ItemCopiedEvent>;

    /**
    
     * A function that is executed before a file or directory is copied.
    
    
     */
    @Output() onItemCopying: EventEmitter<ItemCopyingEvent>;

    /**
    
     * A function that is executed when a file or directory is deleted.
    
    
     */
    @Output() onItemDeleted: EventEmitter<ItemDeletedEvent>;

    /**
    
     * A function that is executed before a file or directory is deleted.
    
    
     */
    @Output() onItemDeleting: EventEmitter<ItemDeletingEvent>;

    /**
    
     * A function that is executed before a file is downloaded.
    
    
     */
    @Output() onItemDownloading: EventEmitter<ItemDownloadingEvent>;

    /**
    
     * A function that is executed when a file or directory is moved.
    
    
     */
    @Output() onItemMoved: EventEmitter<ItemMovedEvent>;

    /**
    
     * A function that is executed before a file or directory is moved.
    
    
     */
    @Output() onItemMoving: EventEmitter<ItemMovingEvent>;

    /**
    
     * A function that is executed when a file or directory is renamed.
    
    
     */
    @Output() onItemRenamed: EventEmitter<ItemRenamedEvent>;

    /**
    
     * A function that is executed before a file or directory is renamed.
    
    
     */
    @Output() onItemRenaming: EventEmitter<ItemRenamingEvent>;

    /**
    
     * A function that is executed after a UI component property is changed.
    
    
     */
    @Output() onOptionChanged: EventEmitter<OptionChangedEvent>;

    /**
    
     * A function that is executed when the selected file is opened.
    
    
     */
    @Output() onSelectedFileOpened: EventEmitter<SelectedFileOpenedEvent>;

    /**
    
     * A function that is executed when a file system item is selected or selection is canceled.
    
    
     */
    @Output() onSelectionChanged: EventEmitter<SelectionChangedEvent>;

    /**
    
     * A function that is executed when a toolbar item is clicked.
    
    
     */
    @Output() onToolbarItemClick: EventEmitter<ToolbarItemClickEvent>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() accessKeyChange: EventEmitter<string | undefined>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() activeStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() allowedFileExtensionsChange: EventEmitter<Array<string>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() contextMenuChange: EventEmitter<dxFileManagerContextMenu>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() currentPathChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() currentPathKeysChange: EventEmitter<Array<string>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() customizeDetailColumnsChange: EventEmitter<((columns: Array<dxFileManagerDetailsColumn>) => Array<dxFileManagerDetailsColumn>)>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() customizeThumbnailChange: EventEmitter<((fileSystemItem: FileSystemItem) => string)>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() disabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() elementAttrChange: EventEmitter<Record<string, any>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() fileSystemProviderChange: EventEmitter<any | null>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() focusedItemKeyChange: EventEmitter<string | undefined>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() focusStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() heightChange: EventEmitter<number | string | undefined>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() hintChange: EventEmitter<string | undefined>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() hoverStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() itemViewChange: EventEmitter<{ details?: { columns?: Array<dxFileManagerDetailsColumn | string> }, mode?: FileManagerItemViewMode, showFolders?: boolean, showParentFolder?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() notificationsChange: EventEmitter<{ showPanel?: boolean, showPopup?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() permissionsChange: EventEmitter<{ copy?: boolean, create?: boolean, delete?: boolean, download?: boolean, move?: boolean, rename?: boolean, upload?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() rootFolderNameChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() rtlEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() selectedItemKeysChange: EventEmitter<Array<string>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() selectionModeChange: EventEmitter<SingleOrMultiple>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() tabIndexChange: EventEmitter<number>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() toolbarChange: EventEmitter<dxFileManagerToolbar>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() uploadChange: EventEmitter<{ chunkSize?: number, maxFileSize?: number }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() visibleChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() widthChange: EventEmitter<number | string | undefined>;




    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper,
            optionHost: NestedOptionHost,
            transferState: TransferState,
            @Inject(PLATFORM_ID) platformId: any) {

        super(elementRef, ngZone, templateHost, _watcherHelper, transferState, platformId);

        this._createEventEmitters([
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'contextMenuItemClick', emit: 'onContextMenuItemClick' },
            { subscribe: 'contextMenuShowing', emit: 'onContextMenuShowing' },
            { subscribe: 'currentDirectoryChanged', emit: 'onCurrentDirectoryChanged' },
            { subscribe: 'directoryCreated', emit: 'onDirectoryCreated' },
            { subscribe: 'directoryCreating', emit: 'onDirectoryCreating' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'errorOccurred', emit: 'onErrorOccurred' },
            { subscribe: 'fileUploaded', emit: 'onFileUploaded' },
            { subscribe: 'fileUploading', emit: 'onFileUploading' },
            { subscribe: 'focusedItemChanged', emit: 'onFocusedItemChanged' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'itemCopied', emit: 'onItemCopied' },
            { subscribe: 'itemCopying', emit: 'onItemCopying' },
            { subscribe: 'itemDeleted', emit: 'onItemDeleted' },
            { subscribe: 'itemDeleting', emit: 'onItemDeleting' },
            { subscribe: 'itemDownloading', emit: 'onItemDownloading' },
            { subscribe: 'itemMoved', emit: 'onItemMoved' },
            { subscribe: 'itemMoving', emit: 'onItemMoving' },
            { subscribe: 'itemRenamed', emit: 'onItemRenamed' },
            { subscribe: 'itemRenaming', emit: 'onItemRenaming' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'selectedFileOpened', emit: 'onSelectedFileOpened' },
            { subscribe: 'selectionChanged', emit: 'onSelectionChanged' },
            { subscribe: 'toolbarItemClick', emit: 'onToolbarItemClick' },
            { emit: 'accessKeyChange' },
            { emit: 'activeStateEnabledChange' },
            { emit: 'allowedFileExtensionsChange' },
            { emit: 'contextMenuChange' },
            { emit: 'currentPathChange' },
            { emit: 'currentPathKeysChange' },
            { emit: 'customizeDetailColumnsChange' },
            { emit: 'customizeThumbnailChange' },
            { emit: 'disabledChange' },
            { emit: 'elementAttrChange' },
            { emit: 'fileSystemProviderChange' },
            { emit: 'focusedItemKeyChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'itemViewChange' },
            { emit: 'notificationsChange' },
            { emit: 'permissionsChange' },
            { emit: 'rootFolderNameChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'selectedItemKeysChange' },
            { emit: 'selectionModeChange' },
            { emit: 'tabIndexChange' },
            { emit: 'toolbarChange' },
            { emit: 'uploadChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {

        return new DxFileManager(element, options);
    }


    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('allowedFileExtensions', changes);
        this.setupChanges('currentPathKeys', changes);
        this.setupChanges('selectedItemKeys', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('allowedFileExtensions');
        this._idh.doCheck('currentPathKeys');
        this._idh.doCheck('selectedItemKeys');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
        super.clearChangedOptions();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }
}

@NgModule({
  imports: [
    DxFileManagerComponent,
    DxoContextMenuModule,
    DxiItemModule,
    DxoItemViewModule,
    DxoDetailsModule,
    DxiColumnModule,
    DxoNotificationsModule,
    DxoPermissionsModule,
    DxoToolbarModule,
    DxiFileSelectionItemModule,
    DxoUploadModule,
    DxiFileManagerColumnModule,
    DxoFileManagerContextMenuModule,
    DxiFileManagerContextMenuItemModule,
    DxoFileManagerDetailsModule,
    DxiFileManagerFileSelectionItemModule,
    DxiFileManagerItemModule,
    DxoFileManagerItemViewModule,
    DxoFileManagerNotificationsModule,
    DxoFileManagerPermissionsModule,
    DxoFileManagerToolbarModule,
    DxiFileManagerToolbarItemModule,
    DxoFileManagerUploadModule,
    DxIntegrationModule,
    DxTemplateModule
  ],
  exports: [
    DxFileManagerComponent,
    DxoContextMenuModule,
    DxiItemModule,
    DxoItemViewModule,
    DxoDetailsModule,
    DxiColumnModule,
    DxoNotificationsModule,
    DxoPermissionsModule,
    DxoToolbarModule,
    DxiFileSelectionItemModule,
    DxoUploadModule,
    DxiFileManagerColumnModule,
    DxoFileManagerContextMenuModule,
    DxiFileManagerContextMenuItemModule,
    DxoFileManagerDetailsModule,
    DxiFileManagerFileSelectionItemModule,
    DxiFileManagerItemModule,
    DxoFileManagerItemViewModule,
    DxoFileManagerNotificationsModule,
    DxoFileManagerPermissionsModule,
    DxoFileManagerToolbarModule,
    DxiFileManagerToolbarItemModule,
    DxoFileManagerUploadModule,
    DxTemplateModule
  ]
})
export class DxFileManagerModule { }

export * from 'devextreme-angular/ui/file-manager/nested';

import type * as DxFileManagerTypes from "devextreme/ui/file_manager_types";
export { DxFileManagerTypes };


